import { confirmToken } from "./auth.js";

/**
 * Real paths, not a hash.
 *
 * Caddy's `try_files` and the service worker's `navigateFallback` both already
 * serve the shell for any in-scope navigation, so a deep link to a list is a
 * URL that can be shared, bookmarked and installed against. Changing that
 * contract means changing both — see the Caddyfile.
 */

export type Route =
  /**
   * `itemId` is the selected row, and it is in the URL rather than in the
   * shell's head for one reason: on a phone the detail surface is a sheet, and
   * the back gesture is how a sheet is closed. Held as state instead, back
   * would leave the list entirely — and an item would not be linkable.
   */
  | { name: "lists" }
  | { name: "list"; id: string; itemId?: string }
  /**
   * The two projections. They carry a selection for the same reason a list
   * does, and they carry their *own* — closing the panel opened from Today
   * returns you to Today rather than teleporting you into the list the row
   * happens to live in.
   */
  | { name: "today"; itemId?: string }
  | { name: "upcoming"; itemId?: string }
  /** Account, appearance, connection and the release (g48). One page, no subpages. */
  | { name: "settings" }
  | { name: "confirm"; token: string }
  /**
   * A shared list, opened from a link (g72). The link itself is the
   * fragment, which no request carries; the route never holds it. `grant`
   * is the link's grant id, in the path of a link made since g131 so that a
   * preview can name the list. It is not the secret, and it must match the
   * fragment's or the page opens nothing.
   */
  | { name: "shared"; grant?: string; itemId?: string }
  /**
   * One item's note, read on a page of its own (g77). `item` is the item
   * being read, and deliberately not `itemId`: that field means "the row
   * whose panel is open" everywhere else, and the shell's Back guard reads
   * it to tell an open panel from a closed one. `editing` is the panel open
   * over the page, so Back closes it exactly as it closes one over a list.
   */
  | { name: "note"; id: string; item: string; editing?: true }
  /**
   * An owner's history (g110): the account's with neither, a list's with
   * `list`, and an item's with both. A deleted list or item keeps its page,
   * which is the one way to read what happened to it.
   */
  | { name: "activity"; list?: string; item?: string }
  | { name: "unknown" };

/** Which grouped projection: read-only views over `due_date`, never a filter. */
export type Agenda = "today" | "upcoming";

/** Only ever used to resolve a path; nothing reads the host back out. */
const BASE = "https://trayflux.invalid";

export function listPath(id: string): string {
  return `/l/${encodeURIComponent(id)}`;
}

export function itemPath(listId: string, itemId: string): string {
  return `${listPath(listId)}/i/${encodeURIComponent(itemId)}`;
}

/** An item's note on its own page (g77), and the same page with Edit open over it. */
export function viewPath(listId: string, itemId: string): string {
  return `${itemPath(listId, itemId)}/view`;
}

export function viewEditPath(listId: string, itemId: string): string {
  return `${viewPath(listId, itemId)}/edit`;
}

/** An owner's history (g110): the account's, a list's or an item's. */
export function activityPath(listId?: string, itemId?: string): string {
  if (listId === undefined) return "/activity";
  return itemId === undefined ? `${listPath(listId)}/activity` : `${itemPath(listId, itemId)}/activity`;
}

/**
 * The shared page, or an item on it (g72), with the page's own fragment kept:
 * the fragment is the link, and a path without it would lose the list on the
 * next reload. Only ever navigated to from the shared page itself.
 */
export function sharedPath(itemId?: string): string {
  // The page's own form, old or new (g131), so a reload opens the same page.
  const grant = SHARED_PATH.exec(window.location.pathname)?.[1];
  const base = grant === undefined ? "/shared" : `/shared/${grant}`;
  const path = itemId === undefined ? base : `${base}/i/${encodeURIComponent(itemId)}`;
  return `${path}${window.location.hash}`;
}

/**
 * A list in the account's Lists that someone shared (g137): its grant's path
 * with no link, which the account's app opens by the session.
 */
export function addedPath(grantId: string): string {
  return `/shared/${grantId}`;
}

/**
 * /shared, /shared/<grant id> (g131), and an item under either. A grant id
 * is exactly 22 characters, so the old item path, /shared/i/<item>, is never
 * read as one.
 */
const SHARED_PATH = /^\/shared(?:\/([A-Za-z0-9_-]{22}))?(?:\/i\/([^/]+))?$/;

export function agendaPath(which: Agenda): string {
  return `/${which}`;
}

export function agendaItemPath(which: Agenda, itemId: string): string {
  return `${agendaPath(which)}/i/${encodeURIComponent(itemId)}`;
}

/**
 * Which screen a route asks for, ignoring what is selected on it.
 *
 * Selecting a row is a navigation, so the shell would otherwise tear the list
 * down and build it again — losing the scroll position, and the place of every
 * row the person was looking at — to open a panel beside it.
 */
export function screenKey(route: Route): string {
  if (route.name === "list") return `list:${route.id}`;
  // By the item alone: an item moved to another list is still the page
  // being read, and its address is replaced rather than the page rebuilt.
  if (route.name === "note") return `note:${route.item}`;
  // An item's history by the item alone, for the same reason as its note.
  if (route.name === "activity") return route.item ? `activity:item:${route.item}` : route.list ? `activity:list:${route.list}` : "activity";
  // One added list and another are two pages, as two lists are (g137).
  if (route.name === "shared") return route.grant === undefined ? "shared" : `shared:${route.grant}`;
  return route.name;
}

/** The row whose panel the route opens, on whichever screen it names. */
export function selection(route: Route): string | undefined {
  if (route.name === "note") return route.editing ? route.item : undefined;
  return "itemId" in route ? route.itemId : undefined;
}

/** The list a route is inside, for the rail's current list. */
export function routeListId(route: Route): string | undefined {
  if (route.name === "activity") return route.list;
  return route.name === "list" || route.name === "note" ? route.id : undefined;
}

export function parseRoute(url: string): Route {
  let parsed: URL;
  try {
    // With a base, so a bare path parses as readily as a full href — internal
    // navigations pass one and `location.href` passes the other.
    parsed = new URL(url, BASE);
  } catch {
    return { name: "unknown" };
  }

  if (parsed.pathname === "/") return { name: "lists" };
  if (parsed.pathname === "/settings") return { name: "settings" };
  if (parsed.pathname === "/activity") return { name: "activity" };

  if (parsed.pathname === "/auth/confirm") {
    const token = confirmToken(parsed.href);
    return token ? { name: "confirm", token } : { name: "unknown" };
  }

  const shared = SHARED_PATH.exec(parsed.pathname);
  if (shared) {
    try {
      const [, grant, itemId] = shared;
      return {
        name: "shared",
        ...(grant === undefined ? {} : { grant }),
        ...(itemId === undefined ? {} : { itemId: decodeURIComponent(itemId) }),
      };
    } catch {
      return { name: "unknown" };
    }
  }

  const agenda = /^\/(today|upcoming)(?:\/i\/([^/]+))?$/.exec(parsed.pathname);
  const which = agenda?.[1];
  if (which === "today" || which === "upcoming") {
    try {
      const itemId = agenda?.[2];
      return itemId === undefined
        ? { name: which }
        : { name: which, itemId: decodeURIComponent(itemId) };
    } catch {
      return { name: "unknown" };
    }
  }

  const note = /^\/l\/([^/]+)\/i\/([^/]+)\/view(\/edit)?$/.exec(parsed.pathname);
  if (note) {
    try {
      const route: Route = { name: "note", id: decodeURIComponent(note[1]!), item: decodeURIComponent(note[2]!) };
      if (note[3]) route.editing = true;
      return route;
    } catch {
      return { name: "unknown" };
    }
  }

  const activity = /^\/l\/([^/]+)(?:\/i\/([^/]+))?\/activity$/.exec(parsed.pathname);
  if (activity) {
    try {
      const route: Route = { name: "activity", list: decodeURIComponent(activity[1]!) };
      if (activity[2] !== undefined) route.item = decodeURIComponent(activity[2]);
      return route;
    } catch {
      return { name: "unknown" };
    }
  }

  const match = /^\/l\/([^/]+)(?:\/i\/([^/]+))?$/.exec(parsed.pathname);
  const id = match?.[1];
  if (id) {
    try {
      const itemId = match?.[2];
      return itemId === undefined
        ? { name: "list", id: decodeURIComponent(id) }
        : { name: "list", id: decodeURIComponent(id), itemId: decodeURIComponent(itemId) };
    } catch {
      // A stray "%" in a hand-typed URL. Not a list id either way.
      return { name: "unknown" };
    }
  }

  return { name: "unknown" };
}

type Handler = (route: Route) => void;

let handler: Handler | undefined;

interface Entry { path: string; state: Record<string, unknown>; index: number }
export interface Navigation {
  from: Route;
  to: Route;
  traversal: boolean;
  own: boolean;
  fromState: Record<string, unknown>;
  toState: Record<string, unknown>;
}
/** Return a question only when this request cannot commit immediately. */
export type NavigationGuard = (navigation: Navigation) =>
  ((proceed: () => void, cancel: () => void) => (() => void) | void) | undefined;
let departureGuard: NavigationGuard | undefined;
const guards = new Set<NavigationGuard>();
const observers = new Set<(navigation: Navigation) => void>();
let accepted: Entry | undefined;
let ownBack = false;
let committing = false;
interface Pending {
  origin: Entry;
  target?: Entry;
  restore: () => void;
  safe?: () => void;
  replay?: boolean;
  unlocated?: boolean;
}
let pending: Pending | undefined;
const INDEX = 'trayfluxNavigationIndex';
const EPOCH = 'trayfluxNavigationEpoch';
let epoch: string | undefined;

function entry(): Entry {
  const state = (window.history.state ?? {}) as Record<string, unknown>;
  // Browser positions also cover entries made by the previous release. Our
  // state counter cannot locate those entries, or infer a traversal direction.
  type BrowserEntry = { index: number; key: string };
  const navigation = (window as Window & { navigation?: {
    currentEntry?: BrowserEntry; entries(): BrowserEntry[];
  } }).navigation;
  const key = navigation?.currentEntry?.key;
  return { path: window.location.href, state, get index() {
    // Evicting old history entries can renumber a retained entry after a push.
    // Its key stays stable; a cached numeric position does not.
    const position = key ? navigation?.entries().find(candidate => candidate.key === key)?.index : undefined;
    if (key) return position !== undefined && position >= 0 ? position : NaN;
    return state[EPOCH] === epoch ? Number(state[INDEX] ?? NaN) : NaN;
  } };
}

function ensureEntry(): void {
  epoch ??= typeof window.history.state?.[EPOCH] === 'string' ? window.history.state[EPOCH] : crypto.randomUUID();
  if (!Number.isFinite(entry().index) || typeof window.history.state?.[INDEX] !== 'number') {
    epoch = crypto.randomUUID();
    window.history.replaceState({ ...window.history.state, [INDEX]: 0, [EPOCH]: epoch }, '', window.location.href);
  }
  accepted = entry();
}

/** History-only entries, such as a popup over the current route. */
export function pushEntry(path: string, state: Record<string, unknown> = {}): void {
  if (!accepted) ensureEntry();
  window.history.pushState({ ...state, [INDEX]: Number(window.history.state[INDEX]) + 1, [EPOCH]: epoch }, '', path);
  accepted = entry();
}

/** A local dismissal whose draft decision has already been made. */
export function back(): void {
  ownBack = true;
  window.history.back();
}

export function addNavigationGuard(guard: NavigationGuard): () => void {
  listen();
  guards.add(guard);
  return () => guards.delete(guard);
}

export function onNavigationCommit(observer: (navigation: Navigation) => void): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

function request(from: Entry, to: Entry, traversal: boolean, own = false): Navigation {
  return { from: parseRoute(from.path), to: parseRoute(to.path), traversal, own,
    fromState: from.state, toState: to.state };
}

function blocker(navigation: Navigation): ReturnType<NavigationGuard> {
  return departureGuard?.(navigation) ?? [...guards].map(guard => guard(navigation)).find(Boolean);
}

function committed(navigation: Navigation): void {
  ensureEntry();
  committing = true;
  try {
    for (const observer of observers) observer(navigation);
    handler?.(currentRoute());
  } finally { committing = false; }
}

/**
 * Older browsers cannot locate history entries from before the upgrade. Keep
 * the draft and URL in the visited slot rather than guessing a direction or
 * calling go(0), which would reload and destroy the draft. A fresh counter
 * epoch prevents this relocated slot colliding with the original one later.
 */
function holdUnlocated(origin: Entry, target: Entry, ask: NonNullable<ReturnType<NavigationGuard>>): void {
  const restore = () => relocate(origin);
  const mine: Pending = { origin, target, restore, unlocated: true };
  pending = mine;
  restore();
  mine.safe = ask(() => {
    if (pending !== mine) return;
    pending = undefined;
    window.history.replaceState({ ...target.state, [INDEX]: 0, [EPOCH]: epoch }, '', target.path);
    committed(request(origin, target, true, true));
  }, () => { if (pending === mine) pending = undefined; }) ?? undefined;
}

function relocate(origin: Entry): void {
  epoch = crypto.randomUUID();
  window.history.replaceState({ ...origin.state, [INDEX]: 0, [EPOCH]: epoch }, '', origin.path);
  accepted = entry();
}

/** A prompt moving focus must not commit a heading's blur-save. */
export function navigationHoldsBlur(target: EventTarget | null): boolean {
  // Closing a dialog can briefly restore focus to the outgoing heading;
  // its removal must not blur-save the very draft just discarded.
  if (pending || committing) return true;
  const anchor = target instanceof Element ? target.closest('a[data-link]') : null;
  return anchor instanceof HTMLAnchorElement &&
    screenKey(parseRoute(anchor.href)) !== screenKey(currentRoute());
}

export function currentRoute(): Route {
  return parseRoute(window.location.href);
}

const OPENER = 'trayfluxOpener';

/**
 * The screen a page was opened from (g77), kept in the page's history entry:
 * it survives a reload and Back and Forward, and a copied address has none.
 * `index` and `epoch` say whether that screen is the entry right behind
 * this one, which is only certain while the app made both.
 */
interface Opener { path: string; index: number; epoch: string }

function readOpener(state: Record<string, unknown>): Opener | undefined {
  const value = state[OPENER] as Partial<Opener> | undefined;
  return typeof value?.path === 'string' && typeof value.index === 'number' && typeof value.epoch === 'string'
    ? value as Opener : undefined;
}

/** Where the current page was opened from, or undefined when it was opened from its address. */
export function opener(): string | undefined {
  return readOpener((window.history.state ?? {}) as Record<string, unknown>)?.path;
}

/**
 * Leaves the page for the screen that opened it: Back, when that screen is
 * the entry behind this one, so history keeps no extra stop; otherwise that
 * screen in this entry's place; and `fallback` when nothing opened it.
 */
export function leave(fallback: string): void {
  if (pending) return;
  ensureEntry();
  const recorded = readOpener(accepted!.state);
  if (recorded && recorded.epoch === epoch && recorded.index === Number(accepted!.state[INDEX]) - 1) {
    back();
    return;
  }
  navigate(recorded?.path ?? fallback, { replace: true, local: true });
}

export function navigate(path: string, options: { replace?: boolean; local?: boolean; opener?: boolean } = {}): void {
  if (pending) return;
  ensureEntry();
  const origin = accepted!;
  const target: Entry = { path: new URL(path, window.location.href).href, state: {}, index: origin.index };
  const navigation = request(origin, target, false, options.local);
  const commit = () => {
    pending = undefined;
    const { popup: _popup, [OPENER]: _opener, ...state } = origin.state;
    // A page records the screen that opened it; a step within the page, such
    // as opening Edit over it, keeps the record it already has.
    const here = new URL(origin.path);
    const kept = options.opener
      ? { path: here.pathname + here.search, index: Number(origin.state[INDEX]), epoch }
      : screenKey(navigation.from) === screenKey(navigation.to) ? readOpener(origin.state) : undefined;
    const record = kept ? { [OPENER]: kept } : {};
    if (options.replace || (origin.state.popup && screenKey(navigation.from) !== screenKey(navigation.to)))
      window.history.replaceState({ ...state, ...record }, '', path);
    else pushEntry(path, record);
    committed(navigation);
  };
  const ask = blocker(navigation);
  if (!ask) { commit(); return; }
  const mine: Pending = { origin, restore: () => {} };
  pending = mine;
  mine.safe = ask(() => { if (pending === mine) commit(); }, () => {
    if (pending === mine) pending = undefined;
  }) ?? undefined;
}

/**
 * Anchors carrying `data-link` are handled here rather than each wiring up its
 * own listener: rows are rebuilt on every change, and a per-anchor listener
 * would be attached and thrown away with them. A modified click still belongs
 * to the browser — "open in new tab" on a list has to keep working.
 */
function interceptClicks(): void {
  document.addEventListener('pointerdown', event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[data-link]') : null;
    if (anchor instanceof HTMLAnchorElement && (!anchor.target || anchor.target === '_self') &&
      navigationHoldsBlur(anchor)) event.preventDefault();
  }, true);
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest("a[data-link]");
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (anchor.target && anchor.target !== "_self") return;

    event.preventDefault();
    navigate(anchor.getAttribute("href") ?? "/");
  });
}

let started = false;

function listen(): void {
  if (!started) {
    started = true;
    window.addEventListener('popstate', () => {
      const target = entry();
      if (pending) {
        const mine = pending;
        if (mine.unlocated) {
          mine.restore();
          mine.safe?.();
        } else if (!Number.isFinite(target.index)) {
          relocate(mine.origin);
          pending = undefined;
          mine.safe?.();
        } else if (mine.replay && target.index === mine.target?.index) {
          pending = undefined;
          committed(request(mine.origin, target, true, true));
        } else if (target.index === mine.origin.index) {
          mine.restore();
        } else {
          // A second Back while asking takes the safe answer after restoring.
          if (mine.safe) mine.restore = () => mine.safe?.();
          window.history.go(mine.origin.index - target.index);
        }
        return;
      }
      const origin = accepted ?? target;
      if (target.index === origin.index && target.path === origin.path) return;
      const navigation = request(origin, target, true, ownBack);
      ownBack = false;
      const ask = blocker(navigation);
      if (!ask) { committed(navigation); return; }
      if (!Number.isFinite(target.index) || !Number.isFinite(origin.index) || target.index === origin.index) {
        holdUnlocated(origin, target, ask);
        return;
      }
      const mine: Pending = { origin, target, restore: () => {} };
      pending = mine;
      mine.restore = () => {
        mine.restore = () => {};
        mine.safe = ask(() => {
          if (pending !== mine) return;
          mine.replay = true;
          window.history.go(target.index - origin.index);
        }, () => { if (pending === mine) pending = undefined; }) ?? undefined;
      };
      window.history.go(origin.index - target.index);
    });
    interceptClicks();
  }
}

export function startRouting(onRoute: Handler, guard?: NavigationGuard): void {
  handler = onRoute;
  departureGuard = guard;
  pending = undefined;
  ownBack = false;
  ensureEntry();
  listen();
  onRoute(currentRoute());
}
